import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Button, TagSelect, type TagSelectOption } from "@coval/ui"
import { Plus, Tag as TagIcon } from "lucide-react"

import { getDefaultTagColor } from "../constants/tag-colors"
import { MAX_TAGS } from "../constants/tag-constraints"
import { useUpdateOrganizationTagColor } from "../routes/useUpdateOrganizationTagColor"
import type { TagSuggestion } from "../types"
import { buildTagColorMap } from "../utils/tagCatalog"
import { normalizeTag, normalizeTags } from "../utils/tagNormalization"
import { TagChip } from "./TagChip"
import { TagColorSwatchPicker } from "./TagColorSwatchPicker"
import { TagNameEditor } from "./TagNameEditor"

interface TagEditorProps {
  tags: string[]
  onTagsChange: (tags: string[]) => void
  suggestions?: TagSuggestion[]
  inputVariant?: "icon" | "tag" | "button"
  inputFirst?: boolean
  maxTags?: number
  maxVisibleTags?: number
  maxOverflowDots?: number
  showInput?: boolean
  allowRemove?: boolean
  allowColorEdit?: boolean
  allowNameEdit?: boolean
  wrap?: boolean
  collapseOverflow?: boolean
  /** Trigger style for the `collapseOverflow` control. */
  triggerVariant?: "secondary" | "ghost"
  /** Renders the `collapseOverflow` control as a labelled button. */
  triggerLabel?: string
  /** Replaces the built-in tag trigger. Must be one focusable element. */
  trigger?: React.ReactElement
  /** Shows the current tags but blocks every edit. */
  disabled?: boolean
  className?: string
}

/**
 * Tag assignment across every surface that carries tags.
 *
 * Two presentations share one props API. `collapseOverflow` surfaces (the
 * table cells) render nothing but the `TagSelect` control, whose trigger
 * carries a colored glyph per tag. Everything else renders readable chips
 * with the control alongside as the way to add.
 *
 * Renaming and recoloring belong to the workspace rather than to any one
 * tagged record, so they are wired into `TagSelect`'s per-option slots here in
 * the app layer where the mutations live: the option's dot recolors the tag,
 * and its label renames it in place.
 */
export const TagEditor: React.FC<TagEditorProps> = ({
  tags,
  onTagsChange,
  suggestions = [],
  inputVariant = "icon",
  inputFirst = false,
  maxTags = MAX_TAGS,
  maxVisibleTags,
  maxOverflowDots = 3,
  showInput = true,
  allowRemove = showInput,
  allowColorEdit = false,
  allowNameEdit = false,
  wrap = true,
  collapseOverflow = false,
  triggerVariant,
  triggerLabel,
  trigger,
  disabled = false,
  className,
}) => {
  // A slot popover portals outside the menu, so the menu has to be told to
  // stay open while one is showing.
  const [openLayerKey, setOpenLayerKey] = useState<string | null>(null)

  const currentTags = useMemo(
    () => normalizeTags(tags, maxTags),
    [maxTags, tags],
  )
  const tagColorMap = useMemo(
    () => buildTagColorMap(suggestions),
    [suggestions],
  )
  const tagIdsByName = useMemo(() => {
    const map: Record<string, string> = {}
    for (const suggestion of suggestions) {
      if (suggestion.id) {
        map[normalizeTag(suggestion.tag)] = suggestion.id
      }
    }
    return map
  }, [suggestions])
  const {
    updateTagColor,
    updateTagName,
    isPending: isTagUpdatePending,
  } = useUpdateOrganizationTagColor()

  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const renamePending = useRef(false)
  const [isRenamePending, setIsRenamePending] = useState(false)
  const generation = useRef(0)
  const isDisabled = disabled || isTagUpdatePending || isRenamePending
  // Do not apply a completed rename to a different editor target or draft.
  const tagsKey = JSON.stringify(currentTags)
  useEffect(() => {
    generation.current += 1
    return () => {
      generation.current += 1
    }
  }, [onTagsChange, tagsKey])

  const handleTagsChange = useCallback(
    (nextTags: string[]) => {
      if (!isDisabled && !renamePending.current) {
        onTagsChange(normalizeTags(nextTags, maxTags))
      }
    },
    [isDisabled, maxTags, onTagsChange],
  )

  const handleCreateTag = useCallback(
    (label: string) => {
      const nextTag = normalizeTag(label)
      if (nextTag && !currentTags.includes(nextTag)) {
        handleTagsChange([...currentTags, nextTag])
      }
    },
    [currentTags, handleTagsChange],
  )

  const handleRenameTag = useCallback(
    async (tag: string, nextLabel: string) => {
      const nextTag = normalizeTag(nextLabel)
      if (isDisabled || renamePending.current || !nextTag || nextTag === tag) {
        return
      }
      const currentGeneration = generation.current
      renamePending.current = true
      setIsRenamePending(true)
      try {
        const saved = await updateTagName(tag, nextTag)
        if (
          saved &&
          currentTags.includes(tag) &&
          generation.current === currentGeneration
        ) {
          onTagsChange(
            normalizeTags(
              currentTags.map((currentTag) =>
                currentTag === tag ? nextTag : currentTag,
              ),
              maxTags,
            ),
          )
        }
      } finally {
        renamePending.current = false
        if (mounted.current) {
          setIsRenamePending(false)
        }
      }
    },
    [currentTags, isDisabled, maxTags, onTagsChange, updateTagName],
  )

  const handleRemoveTag = useCallback(
    (tagToRemove: string) => {
      handleTagsChange(currentTags.filter((tag) => tag !== tagToRemove))
    },
    [currentTags, handleTagsChange],
  )

  // Every tag the menu can offer: the workspace's catalog, plus anything
  // already on this record that the catalog hasn't caught up with.
  const options = useMemo<TagSelectOption[]>(() => {
    const byName = new Map<string, TagSelectOption>()
    for (const suggestion of suggestions) {
      const tag = normalizeTag(suggestion.tag)
      if (tag) {
        byName.set(tag, {
          value: tag,
          label: tag,
          color: suggestion.color ?? getDefaultTagColor(tag),
        })
      }
    }
    for (const tag of currentTags) {
      if (!byName.has(tag)) {
        byName.set(tag, {
          value: tag,
          label: tag,
          color: getDefaultTagColor(tag),
        })
      }
    }
    return Array.from(byName.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    )
  }, [suggestions, currentTags])

  const trackLayer = useCallback((key: string, isOpen: boolean) => {
    setOpenLayerKey((current) => {
      if (isOpen) {
        return key
      }
      return current === key ? null : current
    })
  }, [])

  const renderOptionDot = useCallback(
    (option: TagSelectOption, { color }: { color: string }) => {
      const tag = option.value
      return (
        <TagColorSwatchPicker
          tag={tag}
          color={color}
          disabled={isDisabled || !tagIdsByName[tag]}
          isPending={isTagUpdatePending}
          onColorChange={(nextColor) => updateTagColor(tag, nextColor)}
          onOpenChange={(isOpen) => trackLayer(`color:${tag}`, isOpen)}
        />
      )
    },
    [isDisabled, isTagUpdatePending, tagIdsByName, trackLayer, updateTagColor],
  )

  const renderOptionLabel = useCallback(
    (option: TagSelectOption) => {
      const tag = option.value
      return (
        <TagNameEditor
          tag={tag}
          disabled={isDisabled || !tagIdsByName[tag]}
          isPending={isTagUpdatePending}
          onRename={(nextLabel) => {
            void handleRenameTag(tag, nextLabel)
          }}
          onEditingChange={(isEditing) => trackLayer(`name:${tag}`, isEditing)}
        />
      )
    },
    [handleRenameTag, isDisabled, isTagUpdatePending, tagIdsByName, trackLayer],
  )

  const renderTagSelect = (trigger?: React.ReactElement) => (
    <TagSelect
      options={options}
      value={currentTags}
      onValueChange={handleTagsChange}
      variant={triggerVariant}
      triggerLabel={triggerLabel}
      disabled={isDisabled}
      maxVisible={maxOverflowDots}
      allowCreate={showInput}
      onCreateOption={handleCreateTag}
      maxSelected={maxTags}
      readOnly={!showInput && !allowRemove}
      renderOptionDot={allowColorEdit ? renderOptionDot : undefined}
      renderOptionLabel={allowNameEdit ? renderOptionLabel : undefined}
      keepOpen={openLayerKey !== null}
      trigger={trigger}
    />
  )

  if (collapseOverflow) {
    if (currentTags.length === 0 && !showInput && !allowRemove) {
      return (
        <div
          className={`flex min-w-0 items-center justify-end gap-1.5 ${className ?? ""}`}
        >
          <span className="px-2 text-xs text-coval-text-muted" aria-hidden>
            –
          </span>
        </div>
      )
    }

    return (
      <div
        className={`flex min-w-0 items-center justify-end gap-1.5 ${className ?? ""}`}
      >
        {renderTagSelect(trigger)}
      </div>
    )
  }

  const visibleTags =
    maxVisibleTags === undefined
      ? currentTags
      : currentTags.slice(0, maxVisibleTags)

  /*
   * Chips already name every visible tag here, so the control is only an
   * "add" affordance — a glyph trigger beside them would repeat the same
   * tags twice over.
   */
  const addTrigger =
    inputVariant === "button" ? (
      <Button
        type="button"
        variant="secondary"
        aria-label="Add tag"
        disabled={isDisabled}
      >
        Add Tag
      </Button>
    ) : (
      <Button
        type="button"
        icon
        variant="ghost"
        tooltip="Add tag"
        disabled={isDisabled}
        className={`h-5 w-5 min-w-5 ${inputVariant === "tag" ? "rounded-none" : ""}`}
      >
        {inputVariant === "tag" ? (
          <TagIcon className="h-3 w-3" />
        ) : (
          <Plus className="h-3 w-3" />
        )}
      </Button>
    )

  const tagChips = visibleTags.map((tag) => {
    const chip = (
      <TagChip
        key={tag}
        tag={tag}
        color={tagColorMap[tag]}
        onRemove={
          allowRemove && !isDisabled ? () => handleRemoveTag(tag) : undefined
        }
      />
    )
    return wrap ? (
      chip
    ) : (
      <div key={tag} className="min-w-0 shrink overflow-hidden">
        {chip}
      </div>
    )
  })
  const tagControl = showInput
    ? renderTagSelect(addTrigger)
    : visibleTags.length < currentTags.length
      ? renderTagSelect()
      : null

  return (
    <div
      className={`flex min-w-0 items-center gap-1.5 ${
        wrap ? "flex-wrap" : "flex-nowrap overflow-hidden"
      } ${className ?? ""}`}
    >
      {inputFirst && tagControl}
      {tagChips}
      {!inputFirst && tagControl}
    </div>
  )
}
